/**
 * TikTok Events API Adapter
 *
 * Adapts standardized event payloads into TikTok Events API format.
 * Handles both browser pixel and server-to-server (S2S) data structures.
 *
 * @see https://ads.tiktok.com/marketing_api/docs?id=1701890979375106
 */

(function (pmw, $) {
	"use strict";

	/**
	 * TikTok Event Adapter
	 */
	class TikTokAdapter {
		/**
		 * Adapt event data to TikTok format
		 *
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (e.g., "add_to_cart")
		 * @param {object} context - Additional context (user, page, etc.)
		 * @returns {object|null} TikTok-formatted event data or null if event not supported
		 */
		adapt(eventData, eventName, context) {
			// Only process if TikTok pixel is loaded
			if (!pmwDataLayer?.pixels?.tiktok?.loaded) {
				return null;
			}

			// Map event names to adaptation methods
			const eventMap = {
				page_view       : () => this.adaptPageView(eventData),
				add_to_cart     : () => this.adaptAddToCart(eventData),
				view_item       : () => this.adaptViewContent(eventData),
				begin_checkout  : () => this.adaptInitiateCheckout(eventData),
				add_payment_info: () => this.adaptAddPaymentInfo(eventData),
				purchase        : () => this.adaptPurchase(eventData),
				search          : () => this.adaptSearch(eventData),
				add_to_wishlist : () => this.adaptAddToWishlist(eventData),
				view_category   : () => this.adaptViewCategory(eventData),
				place_order     : () => this.adaptPlaceAnOrder(eventData),
				login           : () => this.adaptLogin(eventData),
			};
			if (eventMap[eventName]) {
				return eventMap[eventName]();
			}

			return null;
		}

		/**
		 * Get base TikTok event data structure
		 *
		 * @returns {object} Base event data with event_id, user, and page
		 */
		getBaseData() {
			return {
				event_id: pmw.getRandomEventId(),
				user    : pmw.tiktokGetEventUserData(),
				page    : pmw.tiktokGetEventPageData(),
			};
		}

		/**
		 * Adapt AddToCart event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} TikTok AddToCart event
		 */
		adaptAddToCart(eventData) {
			const data = this.getBaseData();
			data.event = "AddToCart";

			if (eventData.product) {
				const product   = eventData.product;
				data.properties = {
					value       : product.price * product.quantity,
					currency    : product.currency,
					content_type: "product",
					contents    : [{
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						quantity    : product.quantity,
						price       : product.price,
					}],
				};
			}

			return data;
		}

		/**
		 * Adapt ViewContent event (single product view)
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} TikTok ViewContent event
		 */
		adaptViewContent(eventData) {
			const data = this.getBaseData();
			data.event = "ViewContent";

			if (eventData.product) {
				const product   = eventData.product;
				data.properties = {
					value       : product.price * product.quantity,
					currency    : product.currency,
					content_type: "product",
					contents    : [{
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						quantity    : product.quantity,
						price       : product.price,
					}],
				};
			}

			return data;
		}

		/**
		 * Adapt InitiateCheckout event
		 *
		 * @param {object} eventData - Event data with cart items
		 * @returns {object} TikTok InitiateCheckout event
		 */
		adaptInitiateCheckout(eventData) {
			const data = this.getBaseData();
			data.event = "InitiateCheckout";

			// Use cart data from pmwDataLayer or provided items
			data.properties = pmw.tiktokGetPropertiesFromCart();

			return data;
		}

		/**
		 * Adapt AddPaymentInfo event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} TikTok AddPaymentInfo event
		 */
		adaptAddPaymentInfo(eventData) {
			const data = this.getBaseData();
			data.event = "AddPaymentInfo";

			return data;
		}

		/**
		 * Adapt Purchase event
		 *
		 * @param {object} eventData - Event data with transaction information
		 * @returns {object} TikTok Purchase event
		 */
		adaptPurchase(eventData) {
			const data = this.getBaseData();
			// Use order ID for purchase event to match server-side event ID
			if (pmwDataLayer?.order?.id) {
				data.event_id = pmwDataLayer.order.id.toString();
			}
			data.event = "CompletePayment";

			if (eventData.transaction) {
				const transaction = eventData.transaction;
				data.properties   = {
					value       : transaction.value || 0,
					currency    : transaction.currency || pmwDataLayer.shop.currency,
					content_type: "product",
				};

				if (transaction.items && transaction.items.length > 0) {
					data.properties.contents = transaction.items.map(item => ({
						content_id  : pmw.getProductIdForSpecificPixel(item, "tiktok"),
						content_name: item.name,
						quantity    : item.quantity,
						price       : item.price,
					}));
				}
			}

			return data;
		}

		/**
		 * Adapt Search event
		 *
		 * @param {object} eventData - Event data with search query
		 * @returns {object} TikTok Search event
		 */
		adaptSearch(eventData) {
			const data = this.getBaseData();
			data.event = "Search";

			data.properties = {
				query: pmw.getSearchTermFromUrl(),
			};

			return data;
		}

		/**
		 * Adapt AddToWishlist event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} TikTok AddToWishlist event
		 */
		adaptAddToWishlist(eventData) {
			const data = this.getBaseData();
			data.event = "AddToWishlist";

			if (eventData.product) {
				const product   = eventData.product;
				data.properties = {
					value       : product.price * product.quantity,
					currency    : product.currency,
					content_type: "product",
					contents    : [{
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						quantity    : product.quantity,
						price       : product.price,
					}],
				};
			}

			return data;
		}

		/**
		 * Adapt ViewCategory event (category page view)
		 *
		 * @param {object} eventData - Event data (may be minimal)
		 * @returns {object} TikTok ViewContent event
		 */
		adaptViewCategory(eventData) {
			const data = this.getBaseData();
			data.event = "ViewContent";

			// For category views, we typically don't have detailed product info
			// TikTok will track this as a general content view
			data.properties = {
				content_type: "product_group",
			};

			return data;
		}

		/**
		 * Adapt PageView event
		 *
		 * Note: TikTok doesn't have a specific PageView event in their standard events.
		 * We return null as TikTok doesn't support page view tracking via Events API.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} TikTok doesn't support page view
		 */
		adaptPageView(eventData) {
			// TikTok Events API doesn't have a PageView event
			// Return null to indicate this event is not supported
			return null;
		}

		/**
		 * Adapt PlaceAnOrder event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} TikTok PlaceAnOrder event
		 */
		adaptPlaceAnOrder(eventData) {
			const data = this.getBaseData();
			data.event = "PlaceAnOrder";

			// Use cart data for properties
			data.properties = pmw.tiktokGetPropertiesFromCart();

			return data;
		}

		/**
		 * Adapt Login event
		 *
		 * Note: TikTok doesn't have a standard Login event.
		 * Return null as this event is not supported.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} TikTok doesn't support login event
		 */
		adaptLogin(eventData) {
			// TikTok Events API doesn't have a Login event
			return null;
		}
	}

	// Register the adapter with S2S capabilities
	const adapter = new TikTokAdapter();
	pmw.registerAdapter("tiktok", adapter, {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : false,
		isActive                  : () => pmwDataLayer?.pixels?.tiktok?.loaded === true,
	});

}(window.pmw = window.pmw || {}, jQuery));
